import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { getAccessToken, clearAuthTokens, setAuthTokens } from '@/lib/auth';
import { trpc } from '@/lib/trpc';
import type { LoginResponse } from '@mightybyte/business';

export interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  userRole: string | null;
  userData: any;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userRole, setUserRole] = useState<string | null>(null);

  // Fetch current user data from server
  const { data: userData, isLoading: isUserDataLoading, error: userDataError } = trpc.user.getCurrentUserData.useQuery(
    undefined,
    {
      enabled: isAuthenticated,
    }
  );

  // Handle user data fetch results
  useEffect(() => {
    console.log('userData fetch result:', { userData, userDataError, isUserDataLoading });

    if (userData?.userData) {
      const role = (userData.userData as any).userRole;
      setUserRole(role);
      setIsLoading(false);
      // Navigation is handled by individual route components
    } else if (userDataError) {
      // If we can't fetch user data, sign out
      clearAuthTokens();
      setIsAuthenticated(false);
      setUserRole(null);
      setIsLoading(false);
    } else if (!isUserDataLoading && isAuthenticated && !userData) {
      // Query completed but returned no data
      clearAuthTokens();
      setIsAuthenticated(false);
      setUserRole(null);
      setIsLoading(false);
    }
  }, [userData, userDataError, isUserDataLoading, isAuthenticated]);

  // Listen for login messages from popup via BroadcastChannel
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const BROADCAST_CHANNEL_NAME = 'parenting-first-auth';
    let broadcastChannel: BroadcastChannel | null = null;

    try {
      broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);

      const handleMessage = async (event: MessageEvent) => {
        const message = event.data;

        if (message.type === 'LOGIN_COMPLETE' && message.success) {
          const loginData = message.data as LoginResponse;

          // Store tokens first (async)
          await setAuthTokens(loginData);
          setIsAuthenticated(true);
          // Don't redirect yet - wait for getCurrentUserData to complete
        } else if (message.type === 'LOGIN_ERROR') {
          console.error('Login error:', message.error);
          setIsLoading(false);
        }
      };

      broadcastChannel.addEventListener('message', handleMessage);

      return () => {
        broadcastChannel?.close();
      };
    } catch (error) {
      console.error('BroadcastChannel not supported:', error);
    }
  }, []);

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = await getAccessToken();

      if (token) {
        setIsAuthenticated(true);
        // Don't set isLoading to false yet - wait for user data fetch
      } else {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const logout = async () => {
    await clearAuthTokens();
    setIsAuthenticated(false);
    setUserRole(null);
    setIsLoading(false);
    // Navigation to login is handled by route components
  };

  const isAdmin = userRole === 'admin';

  console.log('AuthProvider state, isLoading:', isLoading, 'isAuthenticated:', isAuthenticated, 'userRole:', userRole, 'isUserDataLoading', isUserDataLoading);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading: isLoading || (isAuthenticated && isUserDataLoading),
        isAdmin,
        userRole,
        userData: userData?.userData,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
